/* eslint-disable no-nested-ternary */
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetPodcastContentList,
  selectPodcastContentPageNumber,
  selectGetPodcastContentState,
  selectPodcastHasMoreContent,
  selectPodcastContentList,
  UpdatePodcastHasMoreContent,
  UpdatePodcastPageNumber,
} from '../../../store/slices/contentSlice';
import { getPodcastContent } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface PodcastContentCategoryProps {
  categoryId: string;
  keyProp: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}

export function PodcastContentCategory({
  categoryId,
  keyProp,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:PodcastContentCategoryProps) {
  const storePodcastContentList = useAppSelector(selectPodcastContentList);
  const podcastContentList = useMemo(() => storePodcastContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storePodcastContentList, contentFeedSection]);

  const getPodcastContentState = useAppSelector(selectGetPodcastContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const podcastPage = useAppSelector(selectPodcastContentPageNumber);
  const podcastHasMore = useAppSelector(selectPodcastHasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: podcastContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentType: 'podcast' },
    getCategoryContent: getPodcastContent,
    resetCategoryContentList: ResetPodcastContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdatePodcastPageNumber,
    updateHasMoreCategoryContent: UpdatePodcastHasMoreContent,
  });

  const isLoading = useMemo(
    () => (getPodcastContentState === 'pending' && !initialFetchDone),
    [getPodcastContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      podcastPage,
      podcastHasMore,
    );
  }, [podcastPage, podcastHasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${keyProp}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${keyProp}-content`}
          categoryId={categoryId}
          storeContentList={podcastContentList}
          onNextPage={onNextPage}
          hasMoreContent={podcastHasMore}
          showSearchBar={true}
        />
      ) : podcastContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${keyProp}-content`}
          categoryTitle="Podcasts"
          storeContentList={podcastContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
